/**
 * External dependencies
 */
import React from "react";
import { size, defaults } from "lodash";
import { Box } from "@towersystems/towersystems-design-system";
import TrackVisibility from "react-on-screen";
import classnames from "classnames";

/**
 * Internal dependencies
 */
import QueryCubeJS from "data/query-cube-js";
import { renderRecharts } from "../utils";
import ReportHeader from "./report-header";
import ReportActions from "./report-actions";
import Icon from "components/icon";
import { useQueryModifier, useQueryModifierProvider } from "lib/query-modifier";
import { QueryModifierContext } from "lib/query-modifier";

/**
 * Style dependencies
 */
import "./styles.scss";

const defaultQuery = {
  measures: [],
  dimensions: [],
  order: {},
  filters: [],
  limit: 5,
  renewQuery: false,
};

const Report = ({
  measures,
  compare = false,
  compareDateRange = undefined,
  applyGlobalFilters = false,
  applyDateRangeFilter = true,
  filters,
  order,
  timeDimensions,
  timeDimension,
  dimensions,
  limit,
  name,
  actions = [],
  children,
  sort,
  renderHeader,
  dataType,
  showActions = true,
  removeReport = () => null,
  deleteReport = () => null,
  draggable = true,
  overrideGlobalFilterCube = false,
  timeDateRange,
  timeGranularity,
  className,
  total = false,
  renewQuery = false,
  smartFilterSelection = true,
  ...p
}) => {
  const maybeParentQueryModifier = useQueryModifierProvider({
    errorOnNotFound: false,
  });

  const queryModifer = useQueryModifier();

  const query = defaults(
    {},
    {
      measures,
      dimensions,
      filters,
      limit:
        limit ??
        (dataType === "time_series" ? 1000 : (limit && parseInt(limit)) || 6),
      order: order
        ? order
        : sort && sort.by && sort.direction
          ? { [sort.by]: sort.direction }
          : {},
      timeDimensions: timeDimensions || [],
      total,
      renewQuery,
    },
    defaultQuery
  );

  const canQueryPreviousRange =
    compare && (dataType === "time_series" || dataType === "single");

  const isTimeSeries = dataType === "time_series";
  const classes = classnames("dashboard-report", className);

  if (size(dimensions) === 0 && size(measures) === 0) {
    return (
      <TrackVisibility once className={classes} offset={800}>
        <div>
          {renderHeader ? (
            renderHeader({ name, className: "dashboard-report__header" })
          ) : (
            <ReportHeader name={name}></ReportHeader>
          )}
          {showActions && actions.length >= 1 ? (
            <ReportActions actions={actions} />
          ) : (
            <div />
          )}
        </div>
      </TrackVisibility>
    );
  }

  return (
    <QueryModifierContext.Provider
      value={maybeParentQueryModifier ?? queryModifer}
    >
      <TrackVisibility once className={classes} offset={800}>
        {({ isVisible }) =>
          isVisible && (
            <>
              {renderHeader ? (
                renderHeader({ name, className: "dashboard-report__header" })
              ) : (
                <ReportHeader name={name}></ReportHeader>
              )}
              {showActions && actions.length >= 1 && (
                <ReportActions actions={actions} />
              )}
              <Box className="dashboard-report__content" display="flex">
                <QueryCubeJS
                  smartFilterSelection={smartFilterSelection}
                  applyDashboardFilters={applyGlobalFilters}
                  queryPreviousRange={canQueryPreviousRange}
                  filterDateRange={applyDateRangeFilter}
                  compareDateRange={compareDateRange}
                  query={query}
                  timeSeries={isTimeSeries}
                  overrideGlobalFilterCube={overrideGlobalFilterCube}
                  forceDateRange={timeDateRange}
                  render={renderRecharts(
                    (response) =>
                      children && children({ query, name, ...response })
                  )}
                  timeDimension={timeDimension}
                  timeGranularity={timeGranularity}
                />
              </Box>
              {draggable && (
                <Box className="dashboard-report--draggable">
                  <Icon name="FiMove" />
                </Box>
              )}
            </>
          )
        }
      </TrackVisibility>
    </QueryModifierContext.Provider>
  );
};

export default Report;
